<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ route.params.id ? 'Editar Taxa de Pagamento' : 'Nova Taxa de Pagamento' }}
      </h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarTaxa" @cancel="voltar">
      <FormSection title="Informações da Taxa">
        <!-- Linha com os selects para Operador, Bandeira e Tipo -->
        <div class="d-flex gap-3 mb-3">
          <!-- Campo Operador -->
          <div class="flex-fill larger-field">
            <SelectField
              id="operador_id"
              v-model="taxa.operador_id"
              :options="operadoresOptions"
              placeholder="Selecione o operador"
              label="Operador <span class='required'>*</span>"
              required
              @update:modelValue="onOperadorChange"
            />
            <div v-if="errors.operador_id" class="error-message">{{ errors.operador_id }}</div>
          </div>
          <!-- Campo Bandeira -->
          <div class="flex-fill larger-field">
            <SelectField
              id="bandeira_id"
              v-model="taxa.bandeira_id"
              :options="bandeirasOptions"
              placeholder="Selecione a bandeira"
              label="Bandeira <span class='required'>*</span>"
              required
            />
            <div v-if="errors.bandeira_id" class="error-message">{{ errors.bandeira_id }}</div>
          </div>
          <!-- Campo Tipo -->
          <div class="flex-fill larger-field">
            <SelectField
              id="tipo_id"
              v-model="taxa.tipo_id"
              :options="tiposOptions"
              placeholder="Selecione o tipo"
              label="Tipo <span class='required'>*</span>"
              required
            />
            <div v-if="errors.tipo_id" class="error-message">{{ errors.tipo_id }}</div>
          </div>
          <InputField
            id="taxa"
            v-model="localTaxa"
            placeholder="Digite a taxa (em %)"
            label="Taxa (%) <span class='required'>*</span>"
            required
            :class="{ 'error-input': errors.taxa }"
            @blur="onTaxaBlur"
          />
          <div v-if="errors.taxa" class="error-message">{{ errors.taxa }}</div>
        </div>
        <!-- O bloco de "Status" foi removido -->
      </FormSection>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import InputField from "@/components/Layout/InputField.vue";
import SelectField from "@/components/Layout/SelectField.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import BaseForm from "@/components/Layout/BaseForm.vue";
import axios from "@/services/axios";

export default {
  components: { InputField, SelectField, FormSection, BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    // Variável reativa para a taxa (propriedade "ativo" removida)
    const taxa = reactive({
      id: null,
      operador_id: '',
      bandeira_id: '',
      tipo_id: '',
      taxa: "",
    });

    // Variável local para o campo de input (mantém o formato "99,99")
    const localTaxa = ref("");

    const errors = reactive({});
    const isSubmitting = ref(false);

    // Operadores e opções
    const operadores = ref([]);
    const operadoresOptions = computed(() =>
      operadores.value.map(op => ({ text: op.operador, value: op.id }))
    );

    // Bandeiras – filtradas pelo operador selecionado
    const bandeiras = ref([]);
    const bandeirasOptions = computed(() =>
      bandeiras.value.map(b => ({ text: b.bandeira, value: b.id }))
    );

    // Tipos – filtrados pelo operador selecionado
    const tipos = ref([]);
    const tiposOptions = computed(() =>
      tipos.value.map(t => ({ text: t.tipo, value: t.id }))
    );

    // Função para buscar operadores
    const fetchOperadores = async () => {
      try {
        const response = await axios.get('/financeiro/parametros/operadores');
        operadores.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar operadores:", error);
      }
    };

    // Função para buscar bandeiras filtradas pelo operador selecionado
    const fetchBandeiras = async (operadorId) => {
      try {
        const response = await axios.get('/financeiro/parametros/bandeiras', {
          params: { operador_id: operadorId }
        });
        bandeiras.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar bandeiras:", error);
      }
    };

    // Função para buscar tipos filtrados pelo operador selecionado
    const fetchTipos = async (operadorId) => {
      try {
        const response = await axios.get('/financeiro/parametros/tipos', {
          params: { operador_id: operadorId }
        });
        tipos.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar tipos:", error);
      }
    };

    // Quando o operador muda, limpar os campos de bandeira e tipo e buscar os dados encadeados
    const onOperadorChange = (newOperadorId) => {
      taxa.bandeira_id = '';
      taxa.tipo_id = '';
      bandeiras.value = [];
      tipos.value = [];
      if (newOperadorId) {
        fetchBandeiras(newOperadorId);
        fetchTipos(newOperadorId);
      }
    };

    // Validação do formulário
    const validateForm = () => {
      errors.operador_id = taxa.operador_id ? "" : "Operador é obrigatório.";
      errors.bandeira_id = taxa.bandeira_id ? "" : "Bandeira é obrigatória.";
      errors.tipo_id = taxa.tipo_id ? "" : "Tipo é obrigatório.";
      errors.taxa = localTaxa.value ? "" : "A taxa é obrigatória.";
    };

    // Utiliza a variável local (localTaxa) para a validação, mantendo o formato "99,99"
    const isFormValid = computed(() => {
      return taxa.operador_id &&
             taxa.bandeira_id &&
             taxa.tipo_id &&
             localTaxa.value.trim() !== "" &&
             !Object.values(errors).some(error => error);
    });

    const novoTaxa = () => {
      Object.assign(taxa, {
        operador_id: '',
        bandeira_id: '',
        tipo_id: '',
        taxa: "",
      });
      localTaxa.value = "";
      for (const key in errors) {
        if (Object.hasOwn(errors, key)) errors[key] = "";
      }
    };

    // Evento blur: converte o valor digitado de "99,99" para um número (99.99) e atualiza taxa.taxa
    const onTaxaBlur = () => {
      const cleaned = localTaxa.value.trim().replace(",", ".");
      const numericValue = parseFloat(cleaned);
      if (!isNaN(numericValue)) {
        taxa.taxa = numericValue;
        localTaxa.value = numericValue.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } else {
        taxa.taxa = "";
        localTaxa.value = "";
      }
    };

    const salvarTaxa = async () => {
      if (isSubmitting.value) return;
      validateForm();
      if (!isFormValid.value) {
        console.error("O formulário contém erros:", errors);
        return;
      }
      const cleaned = localTaxa.value.trim().replace(",", ".");
      const numericValue = parseFloat(cleaned);
      if (isNaN(numericValue)) {
        errors.taxa = "Valor da taxa inválido.";
        return;
      }
      taxa.taxa = numericValue;
      
      isSubmitting.value = true;
      try {
        const method = taxa.id ? "put" : "post";
        const url = taxa.id
          ? `/financeiro/parametros/taxas/${taxa.id}`
          : "/financeiro/parametros/taxas";
        await axios[method](url, {
          operador_id: taxa.operador_id,
          bandeira_id: taxa.bandeira_id,
          tipo_id: taxa.tipo_id,
          taxa: numericValue,
        });
        router.push("/financeiro/parametros/taxas");
      } catch (error) {
        console.error("Erro ao salvar taxa:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push("/financeiro/parametros/taxas");
    };

    watch(
      () => taxa.taxa,
      (newVal) => {
        if (newVal !== "" && !isNaN(newVal)) {
          localTaxa.value = parseFloat(newVal).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        } else {
          localTaxa.value = "";
        }
      },
      { immediate: true }
    );

    onMounted(async () => {
      await fetchOperadores();
      const taxaId = route.params.id;
      if (taxaId) {
        try {
          const response = await axios.get(`/financeiro/parametros/taxas/${taxaId}`);
          Object.assign(taxa, response.data);
          if (taxa.operador_id) {
            await fetchBandeiras(taxa.operador_id);
            await fetchTipos(taxa.operador_id);
          }
        } catch (error) {
          console.error("Erro ao carregar taxa:", error);
        }
      } else {
        novoTaxa();
      }
    });

    return {
      taxa,
      localTaxa,
      onTaxaBlur,
      errors,
      isFormValid,
      isSubmitting,
      salvarTaxa,
      voltar,
      route,
      operadoresOptions,
      bandeirasOptions,
      tiposOptions,
      onOperadorChange,
    };
  },
};
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
.error-input {
  border-color: #dc3545;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
.form-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* Classe para aumentar a largura mínima dos campos */
.larger-field {
  min-width: 180px;
}
</style>
